import React, { useContext, useState, useRef, useLayoutEffect } from "react";
import styled from "@emotion/styled";
import posed, { Transition } from "react-pose";
import Checkbox from "@src/components/forms/Checkbox";
import { Field, Formik } from "formik";
import Select from "@src/components/forms/Select";
import { FormErrors } from "@src/components/forms/helpers";
import axios from "axios";
import cookies from "cookies-js";
import Input from "@src/components/forms/Input";
import Button from "@src/components/Button";
import MailCheck from "react-mailcheck";
import { css } from "@emotion/core";
import { Box, Flex } from "../Boxes";
import { Text } from "@src/components/Text";
import { color, layout, space } from "styled-system";
import { ModalContext, ModalType } from "./ModalController";
import loadingMojo from "@src/assets/images/loadingMojo.gif";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import Translate from "@src/components/translation/Translate";
import { currentLanguage, fixLocale, getRelativePath } from "@src/utils/routes";
import { AppDataContext } from "../AppDataContext";
import { incrementMetric, logEvent as le, setEntityId } from "@src/utils/logClient";
import { useQueryParam, getSearchParams } from "gatsby-query-params";
import { useLocation } from "@reach/router";
import query from "@src/utils/query";
import window from "global/window";
import { theme } from "@classdojo/web/nessie";
import { Subheading, DetailText, DetailAction, Space } from "@src/components/nessie-web";

axios.defaults.withCredentials = true;

const Link = styled.a`
  display: inline-block;
  line-height: 18px;
  font-family: proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-weight: 600;
  color: ${(props) => (props.color ? props.color : "#adadad")};
  text-decoration: none;
  cursor: pointer;
  &:hover,
  &:focus {
    text-decoration: none;
    color: ${(props) => (props.hoverColor ? props.hoverColor : "#00bcf2")};
  }

  ${layout}
  ${space}
  ${color}
`;

const PrivacyLink = styled("a")`
  color: ${theme.colors.dt_aqua50};
`;

const InputInfo = styled("div")`
  text-align: left;
  font-weight: bold;
  color: rgb(0, 174, 239);
  padding-bottom: 1rem;
  padding-left: 1rem;
  padding-top: calc(1rem - 15px);

  ${layout}
`;

const AnimatedInfoMessage = styled(
  posed.div({
    enter: { height: "auto" },
    exit: { height: 0 },
    transition: { default: { ease: "tween", duration: 1000 } },
  }),
)`
  overflow: hidden;
`;

const logEvent = function (eventName) {
  return le({
    eventName: eventName,
    eventValue: "domain_email_signup_overlay_variant",
  });
};

type ApiAnswerState =
  | "success"
  | "api-error"
  | "email-error"
  | "password-matches-email-error"
  | "common-password-error"
  | "email-exists-error"
  | null;

const ApiAnswerDetails = ({
  apiAnswer,
  showLoginModal,
  userType,
}: {
  apiAnswer: ApiAnswerState;
  showLoginModal: () => void;
  userType: string;
}) => {
  if (apiAnswer === null) return null;

  const colorCss =
    apiAnswer === "success"
      ? css`
          background-color: #dff0d8;
          border-color: #d6e9c6;
          color: #3c763d;
          border-radius: 5px;
        `
      : css`
          background-color: #f2dede;
          border-color: #ebccd1;
          color: #a94442;
          border-radius: 5px;
        `;
  return (
    <Box my={3} p={3} textAlign="center" css={colorCss}>
      {apiAnswer === "success" && (
        <Text fontSize="2">
          <Translate path="components.signup_teacher.success" />
          <br />
          <Translate path="components.common.redirect_now" />
        </Text>
      )}
      {apiAnswer === "api-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.signup_teacher.unknown_error" />
          </Text>
        </>
      )}
      {apiAnswer === "email-exists-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.signup_teacher.already_used" />
          </Text>
          <Link
            ml="5px"
            onClick={() => {
              logEvent(`web.${userType}.user_signup.signup_form.login.tap`);
              showLoginModal();
            }}
            color="#00bcf2"
            hoverColor="#00a8d9"
            display="inline"
          >
            <Translate path="components.signup_teacher.mean_to_login" />
          </Link>
        </>
      )}
      {apiAnswer === "email-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.signup_teacher.invalid_email" />
          </Text>
        </>
      )}
      {apiAnswer === "password-matches-email-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.signup_teacher.password_matches_email" />
          </Text>
        </>
      )}
      {apiAnswer === "common-password-error" && (
        <>
          <Text fontSize="2" display="inline">
            <Translate path="components.signup_teacher.common_password" />
          </Text>
        </>
      )}
    </Box>
  );
};

type SignupFormData = {
  title?: string;
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  password?: string;
  leaderRole?: string;
  referralId?: string;
  dataTransferConsentChecked: boolean;
  privacyConsentChecked: boolean;
  dataTransferConsent?: boolean;
};

type SignupFormProps = {
  userType: "teacher" | "school_leader";
};

const SignupForm: React.FC<SignupFormProps> = ({ userType }) => {
  const t = useContext(TranslationContext);
  const appData = useContext(AppDataContext);
  const modalContext = useContext(ModalContext);
  const referalCode = useQueryParam("r", null); // key, defaultValue
  const transferDataQueryParam = useQueryParam("transferData", null); // key, defaultValue

  const needsDataTransferConsent = transferDataQueryParam
    ? transferDataQueryParam !== "false"
    : appData.data.needsDataTransferConsent;

  const { href } = useLocation();
  const params = getSearchParams();

  function showLoginModal() {
    if (userType === "teacher") {
      modalContext.showModal(ModalType.TeacherLogin);
    }
    if (userType === "school_leader") {
      modalContext.showModal(ModalType.SchoolLeaderLogin);
    }
  }

  const [emailSelected, setEmailSelected] = useState<boolean>(false);
  const [passwordSelected, setPasswordSelected] = useState<boolean>(false);
  const [apiAnswer, setApiAnswer] = useState<ApiAnswerState>(null);
  const [showExpandSection, setShowExpandSection] = useState<boolean>(false);
  const [expandedSectionHeight, setExpandedSectionHeight] = useState<number>(0);
  const refContainer = useRef();
  useLayoutEffect(() => {
    if (!refContainer || !refContainer.current) return;
    showExpandSection ? setExpandedSectionHeight(refContainer.current.scrollHeight) : setExpandedSectionHeight(0);
  }, [showExpandSection]);

  const initialValues: SignupFormData = {
    title: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    password: "",
    privacyConsentChecked: false,
    dataTransferConsentChecked: false,
  };
  if (referalCode) {
    initialValues.referralId = referalCode;
    cookies.expire("dojoReferalCode");
    cookies.set("dojoReferalCode", referalCode, { expires: Infinity });
    logEvent(`web.${userType}.user_signup.signup_form.visited_with_invite_code`);
  } else {
    logEvent(`web.${userType}.user_signup.signup_form.visited`);
  }
  le({ eventName: `web.${userType}.user_signup.locale`, eventValue: fixLocale(currentLanguage()) });

  function validateForm(values: SignupFormData) {
    const errors: FormErrors<SignupFormData> = {};
    if (!values.title) {
      errors.title = true;
    }
    if (!values.firstName) {
      errors.firstName = true;
    }
    if (!values.lastName) {
      errors.lastName = true;
    }
    if (!values.password || values.password.length < 8) {
      errors.password = true;
    }
    // eslint-disable-next-line no-useless-escape
    const emailRe = /^(?=\S{3,128}$)[^\s\\;,:]+@[^\s\\;,:\.]{1}[^\s\\;,:]*\.[^\s\\;,:0-9]+$/;
    const emailValue = values.emailAddress;
    if (
      !emailRe.test(emailValue) ||
      /@@/.test(emailValue) ||
      /\.\./.test(emailValue) ||
      // eslint-disable-next-line no-useless-escape
      /[`=<>\(\)\!\/\?#,\[\]\|"\\ğı]/.test(emailValue)
    ) {
      errors.emailAddress = true;
    }

    // eslint-disable-next-line no-control-regex
    const nonAsciiCharRegex = /[^\x00-\x7F]/;
    const local = emailValue && emailValue.split("@")[0];

    if (!local || nonAsciiCharRegex.test(local)) {
      errors.emailAddress = true;
    }

    if (needsDataTransferConsent && !values.dataTransferConsentChecked) {
      errors.dataTransferConsentChecked = true;
    }

    if (needsDataTransferConsent && !values.privacyConsentChecked) {
      errors.privacyConsentChecked = true;
    }

    return errors;
  }
  const [pixelControl, setPixelControl] = useState({ pinterest: false, facebook: false });

  function submitForm(values: SignupFormData, { setSubmitting }) {
    const location = window.location;
    let ORIGIN = location.origin;
    if (process.env.GATSBY_ENV === "development") ORIGIN = "";
    const TEACHER_API = ORIGIN + "/api/teacher";
    le({
      eventName: `web.${userType}.user_signup.signup_form.create_account.tap`,
      eventValue: "domain_email_signup_overlay_variant",
      metadata: appData.data.featureFlags,
    });
    le("externalPage.teacher_or_school_leader.signup.click");
    const signupPath = (location.pathname + location.search).substr(1);

    const attribution = cookies.get("attribution");
    if (attribution == "facebook" && ["school_leader", "teacher"].includes(userType))
      setPixelControl({ ...pixelControl, facebook: true });
    if (attribution == "pinterest") setPixelControl({ ...pixelControl, pinterest: true });

    const postSignupQuery = {
      stage: "select_school",
      from: "www",
      signupSource: "www",
    };
    if (referalCode != null) values.referralId = referalCode;

    const getErrorDetail = (error) => {
      try {
        return error.response.data.error.detail || "";
      } catch (err) {
        return "";
      }
    };

    const postBody = {
      title: values.title,
      firstName: values.firstName,
      lastName: values.lastName,
      emailAddress: values.emailAddress,
      password: values.password,
      role: userType,
      includeUserConfig: true,
      signupPath,
      locale: "en-US",
      dataTransferConsent: needsDataTransferConsent
        ? values.privacyConsentChecked && values.dataTransferConsentChecked
        : undefined,
    };

    axios
      .post(TEACHER_API, postBody, {
        withCredentials: true,
      })
      .then(function (response: any) {
        setApiAnswer("success");

        if (response && response.data) {
          setEntityId(response.data._id).then(function () {
            le({
              eventName: `web.${userType}.user_signup.signup_form.account_created`,
              eventValue: href,
              metadata: { ...params, attribution },
            });
          });
        }

        incrementMetric("external_site.teacher_signup.success");
        axios
          .post(
            ORIGIN + "/api/session",
            {
              password: values.password,
              login: values.emailAddress,
            },
            {
              withCredentials: true,
            },
          )
          .then(() => {
            logEvent(`web.${userType}.user_signup.redirect_to_teach`);
            const POST_SIGNUP_URL = `${process.env.GATSBY_POST_SIGNUP_URL}/onboarding-v3/signup?${query.stringify(
              postSignupQuery,
            )}`;
            window.location.href = POST_SIGNUP_URL;
          })
          .catch(function (error) {
            console.log(error);
          });
      })
      .catch(function (error) {
        const errorDetail = getErrorDetail(error);
        if (errorDetail === "Invalid password: Password cannot match email") {
          logEvent(`web.${userType}.user_signup.signup_form.password_matches_email`);
          incrementMetric("external_site.teacher_signup.password_matches_email");
          setApiAnswer("password-matches-email-error");
        } else if (errorDetail === "Weak password: Password must not contain common passwords.") {
          logEvent(`web.${userType}.user_signup.signup_form.common_password`);
          incrementMetric("external_site.teacher_signup.common_password");
          setApiAnswer("common-password-error");
        } else if (errorDetail.includes("Email already exists")) {
          logEvent(`web.${userType}.user_signup.signup_form.email_exists`);
          incrementMetric("external_site.teacher_signup.email_exists");
          setApiAnswer("email-exists-error");
        } else if (errorDetail === "Please provide a valid email address") {
          logEvent(`web.${userType}.user_signup.signup_form.invalid_email`);
          incrementMetric("external_site.teacher_signup.invalid_email");
          setApiAnswer("email-error");
        } else {
          logEvent(`web.${userType}.user_signup.signup_form.unknown_error`);
          incrementMetric("external_site.teacher_signup.unknown_error");
          setApiAnswer("api-error");
        }
      })
      .then(function () {
        setSubmitting(false);
      });
  }

  return (
    <>
      {pixelControl.pinterest && (
        <img height="1" width="1" src="https://ct.pinterest.com/v3/?tid=2612583504841&event=Signup&noscript=1" />
      )}
      {pixelControl.facebook && (
        <img height="1" width="1" src={`https://www.facebook.com/tr?id=172748793147724&ev=${userType}Registration`} />
      )}
      <Formik
        initialValues={{
          ...initialValues,
          title: t.translate("titles.ms") as string,
          leaderRole: userType === "school_leader" ? "principal" : undefined,
        }}
        validate={validateForm}
        onSubmit={submitForm}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            {isSubmitting && (
              <Box width="50px" height="50px" mb={4} mx="auto">
                <img src={loadingMojo}></img>
              </Box>
            )}
            <Select marginBottom={15} name="title" onChange={handleChange} onBlur={handleBlur} value={values.title}>
              <option>{t.translate("titles.ms")}</option>
              <option>{t.translate("titles.miss")}</option>
              <option>{t.translate("titles.mrs")}</option>
              <option>{t.translate("titles.mr")}</option>
              <option>{t.translate("titles.dr")}</option>
              <option>{t.translate("titles.prof")}</option>
              <option>{t.translate("titles.other")}</option>
            </Select>
            <Input
              rounded
              placeholder={t.translate("pages.home.first_name") as string}
              autoComplete="given-name"
              marginBottom={15}
              name="firstName"
              error={errors.firstName && touched.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.firstName}
            />
            <Input
              rounded
              placeholder={t.translate("pages.home.last_name") as string}
              autoComplete="family-name"
              marginBottom={15}
              name="lastName"
              error={errors.lastName && touched.lastName}
              onChange={(evt) => {
                logEvent("web." + userType + ".user_signup.signup_form.name_entry.fill");
                handleChange(evt);
              }}
              onBlur={handleBlur}
              value={values.lastName}
            />
            <MailCheck email={values.emailAddress}>
              {(suggestion) => (
                <>
                  {suggestion && suggestion.full.toLowerCase() != values.emailAddress.toLowerCase() && !emailSelected && (
                    <Flex justifyContent="center">
                      <InputInfo
                        display="inline-block"
                        onClick={() => setFieldValue("emailAddress", suggestion.full)}
                        css={css`
                          cursor: pointer;
                        `}
                      >
                        {t.translate("components.signup_teacher.suggest_email", { email: suggestion.full })}
                      </InputInfo>
                    </Flex>
                  )}
                  <Input
                    rounded
                    placeholder={t.translate("pages.home.email") as string}
                    autoComplete="email"
                    marginBottom={15}
                    name="emailAddress"
                    error={errors.emailAddress && touched.emailAddress}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setEmailSelected(false);
                      logEvent("web." + userType + ".user_signup.signup_form.email_entry.fill");
                      handleBlur(e);
                    }}
                    onFocus={() => setEmailSelected(true)}
                    value={values.emailAddress}
                  />
                </>
              )}
            </MailCheck>
            <Transition>
              {emailSelected && (
                <AnimatedInfoMessage key="email-info">
                  <InputInfo>{t.translate("components.signup_teacher.use_school_email")}</InputInfo>
                </AnimatedInfoMessage>
              )}
            </Transition>
            <Input
              rounded
              placeholder={t.translate("pages.home.password") as string}
              autoComplete="off"
              marginBottom={15}
              type="password"
              name="password"
              error={errors.password && touched.password}
              onChange={handleChange}
              onBlur={(e) => {
                setPasswordSelected(false);
                handleBlur(e);
              }}
              onFocus={() => setPasswordSelected(true)}
              value={values.password}
            />
            <Transition>
              {passwordSelected && (
                <AnimatedInfoMessage key="password-info">
                  <InputInfo>{t.translate("components.signup_teacher.password_length")}</InputInfo>
                </AnimatedInfoMessage>
              )}
            </Transition>
            {userType === "school_leader" && (
              <Select
                marginBottom={15}
                name="leaderRole"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.leaderRole}
              >
                <option value="principal">{t.translate("school_leader_roles.principal")}</option>
                <option value="assistant_principal">{t.translate("school_leader_roles.assistant_principal")}</option>
                <option value="school_leader">{t.translate("school_leader_roles.school_leader")}</option>
              </Select>
            )}
            {needsDataTransferConsent ? (
              <>
                <Field name="privacyConsentChecked">
                  {({ field }) => (
                    <Checkbox checked={field.value} {...field} name="privacyConsentChecked">
                      <DetailText>
                        {t.translate("safe_harbor.agree")}{" "}
                        <PrivacyLink target="_blank" href={getRelativePath("/privacy/")}>
                          {t.translate("safe_harbor.policy")}
                        </PrivacyLink>{" "}
                        {t.translate("safe_harbor.consent2")}{" "}
                        <PrivacyLink target="_blank" href={getRelativePath("/terms/")}>
                          {t.translate("safe_harbor.terms")}.{" "}
                        </PrivacyLink>
                      </DetailText>
                    </Checkbox>
                  )}
                </Field>
                <Field name="dataTransferConsentChecked">
                  {({ field }) => (
                    <Checkbox checked={field.value} {...field} name="dataTransferConsentChecked">
                      <DetailText>{t.translate("safe_harbor.agree_to_data_transfer")}</DetailText>
                    </Checkbox>
                  )}
                </Field>
                <div
                  ref={refContainer}
                  style={{
                    overflow: "hidden",
                    maxHeight: expandedSectionHeight,
                    transition: "max-height .4s ease",
                    WebkitTransition: "max-height .4s ease",
                    paddingLeft: "22px",
                  }}
                  css={css`
                    a {
                      color: ${theme.colors.dt_aqua50};
                    }
                  `}
                >
                  <Subheading>{t.translate("safe_harbor.how_it_works")}</Subheading>
                  <Space size="s" />
                  <DetailText>{t.translate("safe_harbor.privacy_center")}</DetailText>
                  <Space size="s" />
                  <DetailText>{t.translate("safe_harbor.transparency")}</DetailText>
                  <Space size="s" />
                  <DetailText>{t.translate("safe_harbor.privacy_laws")}</DetailText>
                  <Space size="s" />
                  <Subheading>{t.translate("safe_harbor.in_control")}</Subheading>
                  <Space size="s" />
                  <DetailText>{t.translate("safe_harbor.manage_consent")}</DetailText>
                  <Space size="s" />
                </div>

                <div
                  style={{
                    paddingLeft: "22px",
                  }}
                  css={css`
                    cursor: pointer;
                    width: fit-content;
                  `}
                >
                  <DetailAction color="dt_taro50" onClick={() => setShowExpandSection(!showExpandSection)}>
                    {showExpandSection ? t.translate("safe_harbor.collapse") : t.translate("safe_harbor.expand")}
                  </DetailAction>
                </div>
              </>
            ) : (
              <Box>
                <Text display="inline" fontSize={16} mr="5px">
                  {t.translate("safe_harbor.consent1")}
                </Text>
                <Link
                  target="_blank"
                  href={getRelativePath("/terms/")}
                  display="inline"
                  color="#18c3ff"
                  hoverColor="#0096cb"
                >
                  {t.translate("safe_harbor.terms")}
                </Link>
                <Text display="inline" fontSize={16} mx="5px">
                  {t.translate("safe_harbor.consent2")}
                </Text>
                <Link
                  target="_blank"
                  href={getRelativePath("/privacy/")}
                  display="inline"
                  color="#18c3ff"
                  hoverColor="#0096cb"
                >
                  {t.translate("safe_harbor.policy")}
                </Link>
              </Box>
            )}

            <Button
              big
              width="100%"
              my="15px"
              type="submit"
              id={`${userType}_signup_button`}
              disabled={isSubmitting || Object.keys(errors).length > 0 || Object.keys(touched).length === 0}
            >
              {t.translate("pages.home.sign_up")}
            </Button>
            <ApiAnswerDetails apiAnswer={apiAnswer} showLoginModal={showLoginModal} userType={userType} />
          </form>
        )}
      </Formik>
    </>
  );
};

export default SignupForm;
